<template>
  <article>
    <div v-if="title" class="chart-title">
      {{ title }}
    </div>
    <bar-chart
      :chart-data="chartData"
      :chart-options="chartOptions"
      style="min-height: 382px"
    />
  </article>
</template>

<script>
  import { Bar as BarChart } from 'vue-chartjs'
  import {
    Chart as ChartJS,
    Title,
    Tooltip,
    BarElement,
    CategoryScale,
    PointElement,
    TimeScale,
  } from 'chart.js'

  import 'chartjs-adapter-date-fns'
  import { positioningTooltip } from '@/util/chart'
  import { createSVPBlock } from '@/util/statistics'

  ChartJS.register(
    Title,
    Tooltip,
    BarElement,
    CategoryScale,
    PointElement,
    TimeScale,
  )

  export default {
    name: 'OrdersTimeLoadChart',
    components: { BarChart },
    props: {
      items: {
        type: Array,
        default: () => [],
      },
      title: {
        type: String,
        default: null,
      },
    },
    data () {
      return {
        threeMinutes: 180,
        fiveMinutes: 300,
        times: [
          '10:00:00',
          '11:00:00',
          '12:00:00',
          '13:00:00',
          '14:00:00',
          '15:00:00',
          '16:00:00',
          '17:00:00',
          '18:00:00',
          '19:00:00',
          '20:00:00',
          '21:00:00',
          '22:00:00',
          '23:00:00',
        ],
      }
    },
    computed: {
      chartItems () {
        const items = []

        if (!this.items.length) {
          return items
        }

        const firstItem = this.items[0]

        for (let i = 0; i <= 13; i++) {
          const foundItem = this.items.find((item) => item.time === this.times[i])

          if (foundItem) {
            items.push(foundItem)
          } else {
            items.push({
              date: firstItem.date,
              time: this.times[i],
              canceledOrdersCount: 0,
              acceptedOrdersCount: 0,
              avgConfirmTime: 0,
            })
          }
        }

        return items
      },
      chartOptions () {
        return {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            x: {
              stacked: true,
              grid: {
                display: true,
                drawOnChartArea: false,
              },
            },
            y: {
              stacked: true,
              ticks: {
                stepSize: 1,
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: {
              enabled: false,
              position: 'nearest',
              external: this.externalTooltipHandler,
            },
            roundedBarChart: {
              borderRadius: 10,
            },
          },
        }
      },
      chartData () {
        return {
          labels: this.chartItems.map((item) => item.time.slice(0, 5)),
          datasets: [
            {
              backgroundColor: '#EA306B',
              borderColor: '#EA306B',
              borderRadius: 10,
              borderSkipped: 'bottom',
              data: this.chartItems.map((item) => item.canceledOrdersCount),
            },
            {
              backgroundColor: '#FF9D00',
              hoverBackgroundColor: '#6AAD00',
              borderColor: '#FF9D00',
              hoverBorderColor: '#6AAD00',
              borderRadius: 10,
              borderSkipped: 'bottom',
              data: this.chartItems.map(
                (item) => item.acceptedOrdersCount,
              ),
            },
          ],
        }
      },
    },
    methods: {
      externalTooltipHandler (context) {
        const { chart, tooltip } = context
        const tooltipEl = this.createTooltip(chart)

        if (tooltip.opacity === 0) {
          tooltipEl.style.opacity = 0
          return
        }

        // Remove old children
        while (tooltipEl.firstChild) {
          tooltipEl.firstChild.remove()
        }

        const data = this.chartItems[tooltip.dataPoints[0].dataIndex]
        tooltipEl.appendChild(this.createLeftPart(data))
        tooltipEl.appendChild(this.createRightPart(data))

        positioningTooltip(chart, tooltip, tooltipEl)
      },
      createTooltip (chart) {
        let tooltipEl = chart.canvas.parentNode.querySelector('div')

        if (!tooltipEl) {
          tooltipEl = document.createElement('div')
          tooltipEl.classList.add('tooltip')
          tooltipEl.style.position = 'absolute'
          tooltipEl.style.display = 'flex'
          tooltipEl.style.alignItems = 'center'
          tooltipEl.style.background = '#333232'
          tooltipEl.style.borderRadius = '5px'
          tooltipEl.style.color = 'white'
          tooltipEl.style.opacity = 1
          tooltipEl.style.pointerEvents = 'none'
          tooltipEl.style.position = 'absolute'
          tooltipEl.style.transform = 'translate(0, -50%)'
          tooltipEl.style.transition = 'all .1s ease'
          chart.canvas.parentNode.appendChild(tooltipEl)
        }

        return tooltipEl
      },
      createLeftPart (data) {
        const part = document.createElement('div')
        part.style.padding = '5px 10px'
        part.style.borderRight = '2px solid #7E7E7E'

        const acceptedOrders = document.createElement('div')
        acceptedOrders.style.color = '#FF9D00'
        acceptedOrders.style.fontSize = '18px'
        acceptedOrders.appendChild(
          document.createTextNode(data.acceptedOrdersCount + ' зак.'),
        )

        part.appendChild(this.createDateBlock(data))
        part.appendChild(acceptedOrders)

        return part
      },
      createDateBlock (data) {
        const dateBlock = document.createElement('div')
        dateBlock.style.color = '#D3D5D9'
        dateBlock.style.fontSize = '14px'
        dateBlock.style.lineHeight = '16px'
        // dateBlock.style.paddingRight = '10px'

        const formattedWeekDay = new Date(data.date).toLocaleString('ru', {
          weekday: 'short',
        })
        const formattedTime = data.time.slice(0, 5)
        const formattedDate = new Date(data.date).toLocaleString('ru', {
          day: 'numeric',
          month: 'short',
        })

        dateBlock.appendChild(
          document.createTextNode(
            `${formattedWeekDay.toUpperCase()} ${formattedTime} ${formattedDate}`,
          ),
        )

        return dateBlock
      },
      createRightPart (data) {
        const part = document.createElement('div')
        part.style.paddingRight = '10px'
        part.style.paddingLeft = '10px'

        const canceledOrdersBlock = document.createElement('div')
        canceledOrdersBlock.style.color = '#D3D5D9'
        canceledOrdersBlock.style.fontSize = '14px'

        const canceledOrdersCountSpan = document.createElement('span')
        canceledOrdersCountSpan.style.color = '#EA306B'
        canceledOrdersCountSpan.style.fontSize = '18px'
        canceledOrdersCountSpan.appendChild(
          document.createTextNode(data.canceledOrdersCount),
        )

        canceledOrdersBlock.appendChild(canceledOrdersCountSpan)
        canceledOrdersBlock.appendChild(document.createTextNode(' отмены'))

        part.appendChild(createSVPBlock(data.avgConfirmTime))
        part.appendChild(canceledOrdersBlock)

        return part
      },
    },
  }
</script>

<style lang="less" scoped>
@import 'src/assets/styles/statistic.less';
</style>
